import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import { IRFIConversation } from './rfiMessageSlice';

import { AttachmentList } from '../interfaces/AttachmentObject';
import DepartmentObject from '../interfaces/departmentObject';
import { FinalReportData } from '../interfaces/FinalReportData';
import MessagesObject from '../interfaces/MessagesObject';
import {
  AdditionalInformationProps,
  LinkedReportOption,
  ReportDetailsObject,
  ReportTypeProps,
  TenantNamesProps,
} from '../interfaces/reportData';
import { UserAccessRoles } from '../interfaces/ReportPageAccessRoles';

import InternalCommentObject from '~/interfaces/InternalCommentsObject';
import { ReportAttachementProps } from '~/services/api/reports/types';

export type ReportDataState = {
  report: ReportDetailsObject;
  initialRedactedDescriptionPositions: [number, number][] | null;
  redactedDescriptionPositions: [number, number][] | null;
  departmentList: DepartmentObject[];
  allUserTenant: TenantNamesProps[];
  allReportTypes: ReportTypeProps[];
  userAccessRoles: UserAccessRoles;
  redactInfoState: boolean;
  piiRemovalState: boolean;
  voiceRecording: AttachmentList[];
  linkedReportOptions: LinkedReportOption[];
  descriptionDetectedLanguage: number[];
  translatedDetails: string;
  isDetailsTranslated: boolean;
  translateIsLoading: boolean;
  messages: MessagesObject[];
  rfiMessages: IRFIConversation[];
  decryptedAdditionalInformation: AdditionalInformationProps[];
  rawFinalReport: FinalReportData | null;
  viewFile: boolean;
  viewDetails: ReportAttachementProps;
};

const initialState: ReportDataState = {
  report: {
    pendingPiiRemovalRequestId: 0,
    formType: 0,
    reportId: '',
    referenceId: '',
    priority: 0,
    followers: [],
    reporter: '',
    description: '',
    otherDescriptionDetails: {
      canEditScore: false,
      id: 0,
      riskScore: null,
    },
    country: {
      code: '',
      name: '',
    },
    reportType: {
      id: 0,
      name: '',
      type: 0,
    },
    firstDueDate: '',
    nextReview: '',
    finalDueDate: '',
    assignee: '',
    assigneeName: '',
    createdAt: '',
    tags: [],
    status: 0,
    department: {
      id: 0,
      name: '',
      type: 0,
      classification: '',
    },
    subjectIndividual: '',
    suspectedDepartment: '',
    conversation: '',
    linkedReports: [],
    auditLogs: [],
    internalComments: [],
    additionalInformation: [],
    reportAttachments: [],
    reportInformation: [],
    isCaseLockedEnabled: false,
    completedAt: null,
    isInternalSubmission: false,
    isWhistleblowerFeedbackEnabled: false,
    isAssignee: false,
    reportActionFieldValues: [],
    formattedReportNumber: '',
    reporterLastLogin: '',
    isCaseDoNotPurgeEnabled: false,
    isCasePrivilegedEnabled: false,
    archivedFields: [],
    isDescriptionEditable: false,
    totalRiskScore: 0,
  },
  redactedDescriptionPositions: null,
  initialRedactedDescriptionPositions: null,
  departmentList: [],
  allUserTenant: [],
  allReportTypes: [],
  userAccessRoles: {
    is_email_notification_enabled: false,
    is_allowed_to_reopen_report: false,
    is_allowed_to_request_pii_removal: false,
    is_allowed_to_redact_report: false,
    is_read_only_report: false,
    is_allowed_to_create_final_report_template: false,
    is_allowed_to_download_final_report: false,
    is_allowed_to_download_attachments: false,
    is_totp_enabled: false,
    is_allowed_to_send_messages: false,
    is_allowed_to_request_for_additional_information: false,
    is_allowed_to_generate_final_report: false,
  },
  redactInfoState: false,
  piiRemovalState: false,
  voiceRecording: [],
  linkedReportOptions: [],
  descriptionDetectedLanguage: [],
  translatedDetails: '',
  isDetailsTranslated: false,
  translateIsLoading: false,
  messages: [],
  rfiMessages: [],
  decryptedAdditionalInformation: [],
  rawFinalReport: null,
  viewFile: false,
  viewDetails: {} as ReportAttachementProps,
};

const reportDataSlice = createSlice({
  name: 'reportData',
  initialState,
  reducers: {
    setReport: (state, action: PayloadAction<ReportDetailsObject>) => {
      state.report = action.payload;
    },
    setDepartmentList: (state, action: PayloadAction<DepartmentObject[]>) => {
      state.departmentList = action.payload;
    },
    setTenantUserList: (state, action: PayloadAction<TenantNamesProps[]>) => {
      state.allUserTenant = action.payload;
    },
    setTenantReportTypeList: (
      state,
      action: PayloadAction<ReportTypeProps[]>,
    ) => {
      state.allReportTypes = action.payload;
    },
    setUserAccessRoles: (state, action: PayloadAction<UserAccessRoles>) => {
      state.userAccessRoles = action.payload;
    },
    /**
     * Sets the initial/db state and the current state for redacted information
     */
    setInitialRedactedDescriptionPositions: (
      state,
      action: PayloadAction<[number, number][]>,
    ) => {
      state.redactedDescriptionPositions = action.payload;
      state.initialRedactedDescriptionPositions = action.payload;
    },
    /**
     * Reverts the current state of the redacted information to the initial/db state
     */
    revertInitialRedactedDescriptionPositions: (state) => {
      state.redactedDescriptionPositions =
        state.initialRedactedDescriptionPositions;
    },
    setRedactedDescriptionPositions: (
      state,
      action: PayloadAction<[number, number][]>,
    ) => {
      state.redactedDescriptionPositions = action.payload;
    },
    setRedactInfoState: (state, action: PayloadAction<boolean>) => {
      state.redactInfoState = action.payload;
    },
    setPiiRemovalState: (state, action: PayloadAction<boolean>) => {
      state.piiRemovalState = action.payload;
    },
    setVoiceRecording: (state, action: PayloadAction<AttachmentList[]>) => {
      state.voiceRecording = action.payload;
    },
    setLinkedReportOptions: (
      state,
      action: PayloadAction<LinkedReportOption[]>,
    ) => {
      state.linkedReportOptions = action.payload;
    },
    setDescriptionDetectedLanguage: (
      state,
      action: PayloadAction<number[]>,
    ) => {
      state.descriptionDetectedLanguage = action.payload;
    },
    setTranslatedDetails: (state, action: PayloadAction<string>) => {
      state.translatedDetails = action.payload;
    },
    setIsTranslated: (state, action: PayloadAction<boolean>) => {
      state.isDetailsTranslated = action.payload;
    },
    setTranslateIsLoading: (state, action: PayloadAction<boolean>) => {
      state.translateIsLoading = action.payload;
    },
    updateReportInternalComments: (
      state,
      action: PayloadAction<InternalCommentObject[]>,
    ) => {
      state.report.internalComments = action.payload;
    },
    setDecryptedMessages: (state, action: PayloadAction<MessagesObject[]>) => {
      state.messages = action.payload;
    },
    setRfiMessages: (state, action: PayloadAction<IRFIConversation[]>) => {
      state.rfiMessages = action.payload;
    },
    setRawFinalReport: (state, action: PayloadAction<FinalReportData>) => {
      state.rawFinalReport = action.payload;
    },
    // casemanager attachment file reducer
    setDisplayFile: (state, action: PayloadAction<boolean>) => {
      state.viewFile = action.payload;
    },
    setFileDetails: (
      state,
      action: PayloadAction<ReportAttachementProps | undefined>,
    ) => {
      state.viewDetails = action.payload ?? ({} as ReportAttachementProps);
    },
  },
});

export const {
  setReport,
  setDepartmentList,
  setTenantUserList,
  setTenantReportTypeList,
  setUserAccessRoles,
  revertInitialRedactedDescriptionPositions,
  setInitialRedactedDescriptionPositions,
  setRedactedDescriptionPositions,
  setRedactInfoState,
  setPiiRemovalState,
  setVoiceRecording,
  setLinkedReportOptions,
  setDescriptionDetectedLanguage,
  setTranslatedDetails,
  setIsTranslated,
  setTranslateIsLoading,
  updateReportInternalComments,
  setDecryptedMessages,
  setRfiMessages,
  setRawFinalReport,
  setDisplayFile,
  setFileDetails,
} = reportDataSlice.actions;

export default reportDataSlice.reducer;
